import { rangeOverlap } from '~/services/TimeService';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import { daysOfWeek } from './daysOfWeek';
// SF end_date is inclusive but rangeOverlap is half-open — see
// surchargeBoundaries.ts for the rationale. Without the conversion two
// surcharges that share their boundary day (a.end_date === b.start_date) would
// not be detected as overlapping even though that day is covered by both.
import { toExclusiveEnd } from './surchargeBoundaries';

interface ConflictResult {
  /** Date ranges overlap and at least one weekday is shared */
  hasConflict: boolean;
  /** Both surcharges have accumulate: true — allowed without extra confirmation */
  canAccumulate: boolean;
  /** Overlap with one accumulating and one non-accumulating — requires confirmation */
  mixedAccumulate: boolean;
}

export function analyzeSurchargePair(a: SalesforceSurchargePeriod, b: SalesforceSurchargePeriod): ConflictResult {
  const rangeConflict = rangeOverlap(
    a.start_date,
    toExclusiveEnd(a.end_date),
    b.start_date,
    toExclusiveEnd(b.end_date),
  );

  const daysConflict = daysOfWeek.some((d) => {
    return a[d.key] && b[d.key];
  });

  const hasConflict = rangeConflict && daysConflict;

  if (!hasConflict) {
    return { hasConflict: false, canAccumulate: false, mixedAccumulate: false };
  }

  const canAccumulate = !!a.accumulate && !!b.accumulate;
  const mixedAccumulate = !!a.accumulate !== !!b.accumulate;

  return {
    hasConflict: true,
    canAccumulate,
    mixedAccumulate,
  };
}

function allPairs<T>(arr: Array<T>): Array<[T, T]> {
  return arr.flatMap((a, i) => arr.slice(i + 1).map((b) => [a, b] as [T, T]));
}

/**
 * True when some pair of selected surcharges overlaps (date + weekday) and mixing
 * accumulate / non-accumulate requires user confirmation before import.
 */
export function hasMixedAccumulateOverlap(surcharges: Array<SalesforceSurchargePeriod>): boolean {
  return allPairs(surcharges).some(([a, b]) => analyzeSurchargePair(a, b).mixedAccumulate);
}

/** Surcharges that overlap (date + weekday) but neither has the `overlapping` flag set in Salesforce */
export function getUnmarkedOverlappingSurcharges(
  surcharges: Array<SalesforceSurchargePeriod>,
): Array<SalesforceSurchargePeriod> {
  const ids = new Set(
    allPairs(surcharges)
      .filter(([a, b]) => analyzeSurchargePair(a, b).hasConflict && !a.overlapping && !b.overlapping)
      .flat()
      .map((s) => s.id),
  );
  return surcharges.filter((s) => ids.has(s.id));
}

/** Returns each distinct pair of surcharges that overlap with mixed accumulate flags */
export function getMixedAccumulateConflictPairs(
  surcharges: Array<SalesforceSurchargePeriod>,
): Array<[SalesforceSurchargePeriod, SalesforceSurchargePeriod]> {
  return allPairs(surcharges).filter(([a, b]) => analyzeSurchargePair(a, b).mixedAccumulate);
}
